import { Button, ButtonProps, Dialog, Text, TextField, TrashIcon } from 'components'
import { startCase } from 'lodash-es'
import { useRef, useState } from 'react'
import { useDisclosure } from 'utils'

export interface Props extends ButtonProps {
  onDelete: () => void
  description?: string
  buttonLabel?: string
  onlyIcon?: boolean
}

export default function DeleteConfirmButton({
  onDelete,
  description = 'If you are sure type "Delete" below.',
  onlyIcon,
  buttonLabel = 'Delete',
  variant = 'ghost',
  ...rest
}: Props) {
  const { open, onOpen, onClose } = useDisclosure()
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [isHovered, setIsHovered] = useState<boolean>(false)
  const normalizedInput = text.trim()
  const normalizedButtonLabel = buttonLabel?.trim() ?? ''
  const textMatch = normalizedInput === normalizedButtonLabel && normalizedButtonLabel.length > 0

  function handleDelete() {
    onDelete()
    handleDone()
  }

  function handleDone() {
    setText('')
    onClose()
  }

  return (
    <>
      <Button
        variant={variant}
        {...(variant === 'solid' && { bg: 'red.500', color: 'white', _hover: { bg: 'red.600' } })}
        leftIcon={
          <TrashIcon
            size="sm"
            color={variant === 'solid' ? 'white' : 'red'}
            type={isHovered ? 'solid' : 'outline'}
          />
        }
        onClick={() => onOpen()}
        size="sm"
        borderRadius="sm"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...rest}
      >
        {onlyIcon ? null : buttonLabel}
      </Button>

      <Dialog
        open={open}
        onClose={handleDone}
        bodyProps={{ p: 1, px: 6 }}
        contentProps={{ maxW: 'sm' }}
        footer={
          <Button disabled={!textMatch} onClick={handleDelete} palette="danger">
            {buttonLabel}
          </Button>
        }
        initialFocusEl={() => inputRef.current}
        placement="center"
        title={`${buttonLabel}?`}
      >
        <Text pb={4}>{description}</Text>
        <TextField
          autoFocus
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'Enter' || !textMatch) return

            e.preventDefault()
            handleDelete()
          }}
          placeholder={`Type ${startCase(buttonLabel)}`}
          ref={inputRef}
          value={text}
        />
      </Dialog>
    </>
  )
}
